import { Component, computed, inject } from "@angular/core";
import { AuthService } from "@dcc/vestas-online/auth/data-access";
import { parseStartupFailureReason, StartupFailureReason } from "@dcc/vestas-online/data-access";
import { VdsButtonComponent } from "@dcc/vestas-design-system/angular/components/button";
import { injectQueryParams } from "ngxtension/inject-query-params";
import { createStartupRetryUrl } from "../utils/startup-retry-url";

type ErrorPresentation = {
    title: string;
    message: string;
    primaryActionLabel: string;
};

@Component({
    selector: "vo-shell-error",
    templateUrl: "./error.component.html",
    host: {
        class: "u-contain--layout-size-style absolute inset-0 z-0 flex flex-col overflow-hidden print:static print:z-auto print:block print:overflow-auto"
    },
    imports: [VdsButtonComponent]
})
export class ErrorComponent {
    private readonly authService = inject(AuthService);
    private readonly reasonParam = injectQueryParams("reason");
    private readonly returnUrlParam = injectQueryParams("returnUrl");

    protected readonly failureKind = computed<StartupFailureReason>(() => parseStartupFailureReason(this.reasonParam()));
    private readonly retryUrl = computed(() => createStartupRetryUrl(this.returnUrlParam()));

    protected readonly presentation = computed<ErrorPresentation>(() => {
        switch (this.failureKind()) {
            case "cookie-consent":
                return {
                    title: "Cookie consent unavailable",
                    message:
                        "The required cookie consent service could not be loaded or initialized. VestasOnline cannot continue until it is available.",
                    primaryActionLabel: "Try again"
                };
            case "access":
                return {
                    title: "Access required",
                    message:
                        "Your account is signed in, but no applications have been assigned for this VestasOnline area. If you require access, please contact your local Vestas representative.",
                    primaryActionLabel: "Try again"
                };
            case "network":
                return {
                    title: "Connection problem",
                    message: "We could not reach the VestasOnline services. Check your connection and try again.",
                    primaryActionLabel: "Try again"
                };
            case "backend":
                return {
                    title: "Service temporarily unavailable",
                    message: "VestasOnline is currently having trouble completing your request. Please try again in a moment.",
                    primaryActionLabel: "Try again"
                };
            default:
                return {
                    title: "Something went wrong",
                    message:
                        "VestasOnline could not complete your request. Please try again, or log out and sign back in if the problem continues.",
                    primaryActionLabel: "Try again"
                };
        }
    });

    retryLogin(): void {
        window.location.assign(this.retryUrl());
    }

    logout(): void {
        void this.authService.logout();
    }
}
